import {
  ResolutionDiscount,
  ResolutionExchange,
  ResolutionExchangeCredit,
  ResolutionRefund,
  StatusApproved,
  StatusCreated,
  StatusError,
  StatusPartiallyTreatedLogistician,
  StatusPartiallyTreatedMerchant,
  StatusShipped,
  StatusShipping,
  StatusTreatedLogistician,
  StatusTreatedMerchant,
  StatusWaiting,
} from "@/pages/(app)/returns/list/components/icons/filters-icons";
import { DateFrontFilterValueType } from "@/pages/(app)/returns/providers/return-requests-filters.provider";
import { ShopDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  CalendarPlusIcon,
  Circle,
  CircleUser,
  HandCoins,
  MessageCircle,
  StoreIcon,
  Warehouse,
} from "lucide-react";
import { ElementType, useMemo } from "react";
import { FilterName } from "../list/utils/types";
import { useGetFilters } from "@babackapp/private-api-client/client/use-get-filters";

type Option = {
  label: string;
  value: string | DateFrontFilterValueType;
  Icon?: ElementType;
  background?: string;
  color?: string;
};

export type FilterDefinitionValue = {
  type: "date" | "string";
  category: {
    label: string;
    labelPlural: string;
    Icon?: ElementType;
    color?: string;
  };
  isMultiSelect?: boolean;
  options: Map<string, Option>;
};

export type FilterDefinition = Map<FilterName, FilterDefinitionValue>;

type UseReasonOptionsParams = {
  shop?: ShopDto;
};

function useReasonOptions({ shop }: UseReasonOptionsParams): Option[] {
  return useMemo(() => {
    if (shop) {
      return shop.reasons
        .filter((reason) => reason.active)
        .map((reason) => {
          const lang = shop.preferences.languages[0];
          const reasonValue = reason.values.find(
            (value) => value.lang === lang,
          );

          return {
            value: reason.id!,
            label: reasonValue ? reasonValue.text : reason.values[0].text,
          } satisfies Option;
        });
    }
    return [];
  }, [shop]);
}

function optionToMapOption(options: Option[]): Map<string, Option> {
  return new Map(options.map((option) => [option.value, option]));
}

type UseAssigneeOptionsParams = {
  shop?: ShopDto;
};

function useAssigneeOptions({ shop }: UseAssigneeOptionsParams): Option[] {
  return useMemo(() => {
    if (shop) {
      return shop.users.map((user) => {
        let title: string = "";
        if (user.user?.lastName && user.user.firstName) {
          title = user.user?.firstName + " " + user.user?.lastName;
        } else if (user.user?.email) {
          title = user.user?.email;
        }

        return {
          value: user.userId,
          label: title,
        } satisfies Option;
      });
    }
    return [];
  }, [shop]);
}

type UseFiltersDefinitionParams = {
  shop?: ShopDto;
};

export const statusColors = {
  gray: {
    color: "text-black",
    background: "bg-gray-500/20",
  },
  amber: {
    color: "text-amber-600",
    background: "bg-amber-500/20",
  },
  green: {
    color: "text-green-600",
    background: "bg-green-500/20",
  },
  red: {
    color: "text-destructive",
    background: "bg-destructive/20",
  },
};

export function useFiltersDefinition({
  shop,
}: UseFiltersDefinitionParams): FilterDefinition {
  const t = useTranslation();
  const { data: returnRequestFilters } = useGetFilters(shop?.id ?? "");

  const reasonOptions = useReasonOptions({ shop });

  const warehouseOptions = useMemo((): Option[] => {
    if (!shop?.locations) return [];
    return shop.locations
      .filter((loc) => loc.id && (loc.address?.companyName || loc.address?.address1))
      .map((loc) => ({
        value: loc.id!,
        label: loc.address?.companyName || loc.address?.address1 || "",
      }));
  }, [shop?.locations]);

  // The API lists every store id seen on a return request, so stores the merchant
  // has since removed come back labelled with their raw id.
  const returnStoreOptions = useMemo((): Option[] => {
    const configuredIds = new Set(
      shop?.returnToStore.stores?.map((store) => store.id) ?? [],
    );
    return (returnRequestFilters?.returnStores ?? []).map((store) =>
      configuredIds.has(store.value)
        ? store
        : {
            ...store,
            label: t("returns.filter.deletedStore", { id: store.value }),
          },
    );
  }, [returnRequestFilters?.returnStores, shop?.returnToStore.stores, t]);

  return useMemo(() => {
    const statusOptions = [
      {
        label: t("returnDetail.status.toApproved"),
        Icon: StatusCreated,
        value: "created",
        ...statusColors.gray,
      },
      {
        label: t("returnDetail.status.approved"),
        Icon: StatusApproved,
        value: "approved",
        ...statusColors.gray,
      },
      {
        label: t("returnDetail.status.waiting"),
        Icon: StatusWaiting,
        value: "waiting",
        ...statusColors.gray,
      },
      {
        label: t("returnDetail.status.shipping"),
        Icon: StatusShipping,
        value: "shipping",
        ...statusColors.gray,
      },
      {
        label: t("returnDetail.status.shipped"),
        Icon: StatusShipped,
        value: "shipped",
        ...statusColors.gray,
      },
      {
        label: t("returnDetail.status.cancelled"),
        Icon: StatusShipped,
        value: "cancelled",
        ...statusColors.gray,
      },
      {
        label: t("returnDetail.status.partiallyTreatedMerchant"),
        Icon: StatusPartiallyTreatedMerchant,
        value: "partiallyTreatedMerchant",
        ...statusColors.amber,
      },
      {
        label: t("returnDetail.status.treatedMerchant"),
        Icon: StatusTreatedMerchant,
        value: "treatedMerchant",
        ...statusColors.green,
      },
      {
        label: t("returnDetail.status.error"),
        Icon: StatusError,
        value: "error",
        ...statusColors.red,
      },
      ...(shop?.workflowLogistician.activated
        ? [
            {
              label: t("returnDetail.status.partiallyTreatedLogistician"),
              Icon: StatusPartiallyTreatedLogistician,
              value: "partiallyTreatedLogistician",
              ...statusColors.gray,
            },
            {
              label: t("returnDetail.status.treatedLogistician"),
              Icon: StatusTreatedLogistician,
              value: "treatedLogistician",
              ...statusColors.gray,
            },
          ]
        : []),
    ];

    const resolutionOptions = [
      {
        label: t("misc.refund"),
        value: "refund",
        Icon: ResolutionRefund,
      },
      {
        label: t("misc.exchange"),
        value: "exchange",
        Icon: ResolutionExchange,
      },
      {
        label: t("misc.exchange-credit"),
        value: "exchange-credit",
        Icon: ResolutionExchangeCredit,
      },
      {
        label: t("misc.discount"),
        value: "discount",
        Icon: ResolutionDiscount,
      },
    ];
    const createdDateOptions = [
      {
        label: t("returnDetail.creationDate.oneDayAgo"),
        value: "1d",
      },
      {
        label: t("returnDetail.creationDate.threeDaysAgo"),
        value: "3d",
      },
      {
        label: t("returnDetail.creationDate.oneWeekAgo"),
        value: "1w",
      },
      {
        label: t("returnDetail.creationDate.twoWeeksAgo"),
        value: "2w",
      },
      {
        label: t("returnDetail.creationDate.oneMonthAgo"),
        value: "1m",
      },
      {
        label: t("returnDetail.creationDate.twoMonthsAgo"),
        value: "2m",
      },
      {
        label: t("returnDetail.creationDate.threeMonthsAgo"),
        value: "3m",
      },
      {
        label: t("returnDetail.creationDate.sixMonthsAgo"),
        value: "6m",
      },
    ];

    return new Map([
      [
        "status",
        {
          type: "string",
          isMultiSelect: true,
          category: {
            label: t("returns.filter.status"),
            labelPlural: "statuses",
            Icon: Circle,
          },
          options: optionToMapOption(statusOptions),
        },
      ],
      [
        "resolution",
        {
          type: "string",
          isMultiSelect: true,
          category: {
            label: t("returns.filter.resolution"),
            labelPlural: "resolutions",
            Icon: HandCoins,
          },
          options: optionToMapOption(resolutionOptions),
        },
      ],
      [
        "reason",
        {
          type: "string",
          isMultiSelect: true,
          category: {
            label: t("returns.filter.reason"),
            labelPlural: "reasons",
            Icon: MessageCircle,
          },
          options: optionToMapOption(reasonOptions),
        },
      ],
      [
        "assignee",
        {
          type: "string",
          isMultiSelect: true,
          category: {
            label: t("returns.filter.assignee"),
            labelPlural: "assignees",
            Icon: CircleUser,
          },
          options: optionToMapOption(returnRequestFilters?.assignees ?? []),
        },
      ],
      [
        "createdDate",
        {
          type: "date",
          category: {
            label: t("returns.filter.created"),
            labelPlural: "created dates",
            Icon: CalendarPlusIcon,
          },
          options: optionToMapOption(createdDateOptions),
        },
      ],
      [
        "treatedMerchantDate",
        {
          type: "date",
          category: {
            label: t("returns.filter.treatedMerchant"),
            labelPlural: "treated merchant dates",
            Icon: CalendarPlusIcon,
          },
          options: optionToMapOption(createdDateOptions),
        },
      ],
      [
        "treatedLogisticianDate",
        {
          type: "date",
          category: {
            label: t("returns.filter.treatedLogistician"),
            labelPlural: "treated logistician dates",
            Icon: CalendarPlusIcon,
          },
          options: optionToMapOption(createdDateOptions),
        },
      ],
      [
        "returnStore",
        {
          type: "string",
          isMultiSelect: true,
          category: {
            label: t("returns.filter.returnStore"),
            labelPlural: "return stores",
            Icon: StoreIcon,
          },
          options: optionToMapOption([
            {
              label: t("returns.filter.allStores"),
              value: "*",
              Icon: StoreIcon,
            },
            ...returnStoreOptions,
          ]),
        },
      ],
      [
        "warehouse",
        {
          type: "string",
          isMultiSelect: true,
          category: {
            label: t("returns.filter.warehouse"),
            labelPlural: "warehouses",
            Icon: Warehouse,
          },
          options: optionToMapOption([
            {
              label: t("returns.filter.allWarehouses"),
              value: "*",
              Icon: Warehouse,
            },
            ...warehouseOptions,
          ]),
        },
      ],
    ]);
  }, [
    t,
    shop?.workflowLogistician.activated,
    reasonOptions,
    returnRequestFilters,
    returnStoreOptions,
    warehouseOptions,
  ]);
}
